import { useState, useEffect, useCallback } from 'react';
import { waitlistApi, patientsApi, departmentsApi, formatApiError } from '../lib/api';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Clock,
  Plus,
  Trash2,
  ArrowUpCircle,
  CalendarPlus,
  Search,
  Loader2,
  AlertTriangle,
  Users,
  Bell,
  CheckCircle2,
  X
} from 'lucide-react';
import { toast } from 'sonner';

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={`stat-${label.toLowerCase().replace(/ /g, '-')}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-800">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

function AddToWaitlistModal({ open, onClose, onSuccess }) {
  const [patients, setPatients] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [priority, setPriority] = useState('normale');
  const [notes, setNotes] = useState('');
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadDepartments();
    }
  }, [open]);

  const loadDepartments = async () => {
    try {
      const res = await departmentsApi.list();
      setDepartments(res.data);
    } catch (err) { console.error(err); }
  };

  const searchPatients = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) { setPatients([]); return; }
    setSearchLoading(true);
    try {
      const res = await patientsApi.list(query);
      setPatients(res.data);
    } catch (err) { console.error(err); }
    finally { setSearchLoading(false); }
  };

  const handleDepartmentChange = (deptId) => {
    setSelectedDepartment(deptId);
    setSelectedService('');
    const dept = departments.find(d => d.id === deptId);
    setServices(dept?.services || []);
  };

  const handleSubmit = async () => {
    if (!selectedPatient || !selectedService || !selectedDepartment) {
      toast.error('Compilare tutti i campi obbligatori');
      return;
    }
    setSaving(true);
    try {
      await waitlistApi.add({
        patient_id: selectedPatient.id,
        service: selectedService,
        department_id: selectedDepartment,
        priority,
        notes
      });
      toast.success('Paziente aggiunto alla lista d\'attesa');
      onSuccess();
      handleClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const handleClose = () => {
    setSelectedPatient(null);
    setSearchQuery('');
    setPatients([]);
    setSelectedDepartment('');
    setSelectedService('');
    setServices([]);
    setPriority('normale');
    setNotes('');
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="add-waitlist-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>Aggiungi a Lista d'Attesa</h3>
          <button onClick={handleClose} className="text-slate-400 hover:text-slate-600" data-testid="close-modal-btn"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Patient Search */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Paziente *</label>
            {selectedPatient ? (
              <div className="flex items-center justify-between bg-sky-50 border border-sky-200 rounded-lg px-3 py-2">
                <div>
                  <p className="text-sm font-medium text-slate-800">{selectedPatient.name}</p>
                  <p className="text-xs text-slate-500">{selectedPatient.phone}</p>
                </div>
                <button onClick={() => setSelectedPatient(null)} className="text-slate-400 hover:text-red-500">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => searchPatients(e.target.value)}
                  placeholder="Cerca paziente per nome o telefono..."
                  className="pl-9"
                  data-testid="search-patient-input"
                />
                {searchLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />}
                {patients.length > 0 && !selectedPatient && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                    {patients.map(p => (
                      <button
                        key={p.id}
                        onClick={() => { setSelectedPatient(p); setPatients([]); setSearchQuery(''); }}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                        data-testid={`patient-option-${p.id}`}
                      >
                        <p className="text-sm font-medium text-slate-800">{p.name}</p>
                        <p className="text-xs text-slate-500">{p.phone}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Department */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Reparto *</label>
            <select
              value={selectedDepartment}
              onChange={(e) => handleDepartmentChange(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-200 focus:border-sky-400 outline-none"
              data-testid="select-department"
            >
              <option value="">Seleziona reparto...</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>

          {/* Service */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Servizio *</label>
            <select
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-200 focus:border-sky-400 outline-none"
              disabled={!selectedDepartment}
              data-testid="select-service"
            >
              <option value="">Seleziona servizio...</option>
              {services.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Priority */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Priorità</label>
            <div className="flex gap-2">
              <button
                onClick={() => setPriority('normale')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-all ${
                  priority === 'normale'
                    ? 'bg-sky-50 border-sky-300 text-sky-700'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
                data-testid="priority-normale"
              >Normale</button>
              <button
                onClick={() => setPriority('urgente')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-all ${
                  priority === 'urgente'
                    ? 'bg-amber-50 border-amber-300 text-amber-700'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
                data-testid="priority-urgente"
              >
                <span className="flex items-center justify-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> Urgente
                </span>
              </button>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Note (opzionale)</label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Preferenze orarie, note particolari..."
              data-testid="waitlist-notes-input"
            />
          </div>
        </div>
        <div className="p-5 border-t border-slate-200 flex gap-3">
          <Button variant="outline" onClick={handleClose} className="flex-1" data-testid="cancel-add-btn">Annulla</Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !selectedPatient || !selectedService || !selectedDepartment}
            className="flex-1 bg-sky-500 hover:bg-sky-600 text-white gap-2"
            data-testid="confirm-add-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Aggiungi
          </Button>
        </div>
      </div>
    </div>
  );
}

function ConvertModal({ open, entry, onClose, onSuccess }) {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [saving, setSaving] = useState(false);

  const handleConvert = async () => {
    if (!date || !time) {
      toast.error('Selezionare data e ora');
      return;
    }
    setSaving(true);
    try {
      await waitlistApi.convert(entry.id, { date, time });
      toast.success('Appuntamento creato dalla lista d\'attesa!');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  if (!open || !entry) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="convert-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>Converti in Appuntamento</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <p className="text-sm font-medium text-slate-800">{entry.patient_name}</p>
            <p className="text-xs text-slate-500">{entry.service} - {entry.department_name}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Data *</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="convert-date-input" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Ora *</label>
            <select
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-200 focus:border-sky-400 outline-none"
              data-testid="convert-time-input"
            >
              <option value="">Seleziona orario...</option>
              {["09:00","09:30","10:00","10:30","11:00","11:30","14:00","14:30","15:00","15:30","16:00","16:30","17:00"].map(t =>
                <option key={t} value={t}>{t}</option>
              )}
            </select>
          </div>
        </div>
        <div className="p-5 border-t border-slate-200 flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Annulla</Button>
          <Button
            onClick={handleConvert}
            disabled={saving || !date || !time}
            className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white gap-2"
            data-testid="confirm-convert-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />}
            Prenota
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Waitlist() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('waiting');
  const [filterService, setFilterService] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [convertEntry, setConvertEntry] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [entriesRes, statsRes] = await Promise.all([
        waitlistApi.list({ status: filterStatus === '' ? null : filterStatus, service: filterService || undefined }),
        waitlistApi.stats()
      ]);
      setEntries(entriesRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  }, [filterStatus, filterService]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await waitlistApi.remove(id);
      toast.success('Rimosso dalla lista d\'attesa');
      loadData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setDeletingId(null); }
  };

  const handleTogglePriority = async (entry) => {
    const newPriority = entry.priority === 'urgente' ? 'normale' : 'urgente';
    try {
      await waitlistApi.update(entry.id, { priority: newPriority });
      toast.success(`Priorità cambiata: ${newPriority}`);
      loadData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const statusLabels = {
    waiting: 'In Attesa',
    notified: 'Notificato',
    converted: 'Convertito',
    '': 'Tutti'
  };

  return (
    <Layout>
      <PageHeader
        title="Lista d'Attesa"
        subtitle="Gestisci i pazienti in attesa di uno slot"
        actions={
          <Button onClick={() => setShowAddModal(true)} className="bg-sky-500 hover:bg-sky-600 text-white gap-2" data-testid="add-waitlist-btn">
            <Plus className="w-4 h-4" /> Aggiungi
          </Button>
        }
      />

      <div className="content-area" data-testid="waitlist-page">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <StatCard icon={Clock} label="In Attesa" value={stats.total_waiting} color="bg-amber-500" />
            <StatCard icon={Bell} label="Notificati" value={stats.total_notified} color="bg-sky-500" />
            <StatCard icon={CheckCircle2} label="Convertiti" value={stats.total_converted} color="bg-emerald-500" />
            <StatCard icon={AlertTriangle} label="Urgenti" value={stats.urgent} color="bg-red-500" />
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {['waiting', 'notified', 'converted', ''].map(s => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  filterStatus === s ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid={`filter-${s || 'all'}`}
              >
                {statusLabels[s]}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={filterService}
              onChange={(e) => setFilterService(e.target.value)}
              placeholder="Filtra per servizio..."
              className="pl-9 text-sm"
              data-testid="filter-service-input"
            />
          </div>
        </div>

        {/* Entries */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16 bg-white border border-slate-200 rounded-xl">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">Nessun paziente in lista d'attesa</p>
            <Button onClick={() => setShowAddModal(true)} variant="outline" className="mt-4 gap-2" data-testid="add-waitlist-empty-btn">
              <Plus className="w-4 h-4" /> Aggiungi il primo
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {entries.map(entry => (
              <div
                key={entry.id}
                className={`bg-white border rounded-xl p-4 shadow-sm transition-all hover:shadow-md ${
                  entry.priority === 'urgente' ? 'border-amber-200 bg-amber-50/30' :
                  entry.status === 'notified' ? 'border-sky-200 bg-sky-50/30' :
                  entry.status === 'converted' ? 'border-emerald-200 bg-emerald-50/30' :
                  'border-slate-200'
                }`}
                data-testid={`waitlist-entry-${entry.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {/* Priority indicator */}
                    <div className={`w-1.5 h-12 rounded-full flex-shrink-0 ${
                      entry.priority === 'urgente' ? 'bg-amber-400' : 'bg-slate-300'
                    }`} />

                    {/* Patient info */}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm text-slate-800 truncate">{entry.patient_name}</p>
                        {entry.priority === 'urgente' && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium flex-shrink-0">
                            <AlertTriangle className="w-2.5 h-2.5" /> Urgente
                          </span>
                        )}
                        {entry.status === 'notified' && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 font-medium flex-shrink-0">
                            <Bell className="w-2.5 h-2.5" /> Notificato
                          </span>
                        )}
                        {entry.status === 'converted' && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium flex-shrink-0">
                            <CheckCircle2 className="w-2.5 h-2.5" /> Convertito
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">{entry.patient_phone}</p>
                    </div>

                    {/* Service */}
                    <div className="hidden sm:block flex-shrink-0">
                      <p className="text-sm font-medium text-slate-700">{entry.service}</p>
                      <p className="text-xs text-slate-400">{entry.department_name}</p>
                    </div>

                    {/* Date added */}
                    <div className="hidden md:block text-right flex-shrink-0">
                      <p className="text-xs text-slate-400">Inserito il</p>
                      <p className="text-xs font-medium text-slate-600">
                        {new Date(entry.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                    </div>

                    {/* Notes */}
                    {entry.notes && (
                      <div className="hidden lg:block flex-shrink-0 max-w-[150px]">
                        <p className="text-xs text-slate-400 truncate" title={entry.notes}>{entry.notes}</p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {entry.status === 'waiting' || entry.status === 'notified' ? (
                    <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
                      <button
                        onClick={() => handleTogglePriority(entry)}
                        className={`p-2 rounded-lg transition-colors ${
                          entry.priority === 'urgente'
                            ? 'bg-amber-100 text-amber-600 hover:bg-amber-200'
                            : 'bg-slate-100 text-slate-400 hover:bg-amber-50 hover:text-amber-600'
                        }`}
                        title={entry.priority === 'urgente' ? 'Rimuovi urgenza' : 'Segna urgente'}
                        data-testid={`toggle-priority-${entry.id}`}
                      >
                        <ArrowUpCircle className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setConvertEntry(entry)}
                        className="p-2 rounded-lg bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors"
                        title="Converti in appuntamento"
                        data-testid={`convert-${entry.id}`}
                      >
                        <CalendarPlus className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(entry.id)}
                        disabled={deletingId === entry.id}
                        className="p-2 rounded-lg bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-600 transition-colors"
                        title="Rimuovi dalla lista"
                        data-testid={`delete-waitlist-${entry.id}`}
                      >
                        {deletingId === entry.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AddToWaitlistModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={loadData}
      />

      <ConvertModal
        open={!!convertEntry}
        entry={convertEntry}
        onClose={() => setConvertEntry(null)}
        onSuccess={loadData}
      />
    </Layout>
  );
}
